import type { GlCode } from '@/types/models';

/**
 * Split-bank parent detection (#357). A "split" bank account is one whose GL
 * code (e.g. 1030) has sub-GL children (e.g. 1030.05) that are themselves bank
 * accounts. The top-level GL then becomes a non-postable roll-up: activity must
 * land on a sub-account, never the parent.
 *
 * A GL id is a "bank-parent" when it is BOTH:
 *   1. referenced by some bank account's `gl_code_id` (it IS a bank), AND
 *   2. the parent of at least one other GL code (`parentId` points at it).
 *
 * SCOPE = bank/cash GLs only. A non-bank parent (e.g. 7000 expense with a
 * 7000.95 child) is deliberately NOT flagged — those sub-codes are the ordinary
 * chart-of-accounts hierarchy, not split bank accounts. This module is RN-free
 * so it can be unit-tested in plain Vitest and shares its logic with the
 * server-side guard in `api/accounting/post-ledger.ts`.
 */

type GlLike = Pick<GlCode, 'id' | 'parentId'>;

const asSet = (bankGlIds: ReadonlySet<string> | Iterable<string>): ReadonlySet<string> =>
	bankGlIds instanceof Set ? bankGlIds : new Set(bankGlIds);

/**
 * True when `glId` is a bank-parent: it is a bank account's GL AND has at least
 * one child GL in `glCodes`. False for null/undefined, non-bank GLs, the child
 * sub-accounts themselves, and standalone (childless) bank GLs.
 */
export function isBankParentGlId(
	glId: string | null | undefined,
	glCodes: GlLike[],
	bankGlIds: ReadonlySet<string> | Iterable<string>,
): boolean {
	if (!glId) return false;
	if (!asSet(bankGlIds).has(glId)) return false;
	return glCodes.some((g) => g.parentId === glId);
}

/**
 * The set of every bank-parent GL id — computed in one pass so pickers can
 * filter their option lists cheaply. A GL id qualifies when it is a bank
 * account's GL and some other GL names it as `parentId`.
 */
export function bankParentGlIdSet(
	glCodes: GlLike[],
	bankGlIds: ReadonlySet<string> | Iterable<string>,
): Set<string> {
	const bankSet = asSet(bankGlIds);
	const withChildren = new Set<string>();
	for (const g of glCodes) {
		if (g.parentId) withChildren.add(g.parentId);
	}
	const out = new Set<string>();
	for (const id of bankSet) {
		if (withChildren.has(id)) out.add(id);
	}
	return out;
}
